import { Injectable } from '@angular/core';
import {from, Observable, throwError} from "rxjs";
import {catchError, map, switchMap, tap} from "rxjs/operators";
import {environment} from "../../../environments/environment";
import {HttpClient, HttpEventType} from "@angular/common/http";
import {plainToClass, plainToClassFromExist} from "class-transformer";
import {ResultListApiModel} from "../models/result-list.api.model";
import {QueryParamsApiModel} from "../models/query-params-api.model";
import {QueryParamsService} from "../../core/services/query-params.service";
import {MediaTypeEnum} from "../../core/enums/media-type.enum";
import {DocumentApiModel} from "../models/document.api-model";
import {UploadPreprocessService} from "../../core/services/upload-preprocess.service";
import {UploadPreprocessCategory} from "../../core/interfaces/prepared-upload.interface";
import {BackgroundUploadOrchestratorService} from "../../core/services/background-upload-orchestrator.service";

@Injectable({
  providedIn: 'root'
})
export class DocumentRestService {

  private readonly resource: string = '/documents';
  totalItems: number = 0;

  constructor(
    private http: HttpClient,
    private params: QueryParamsService,
    private readonly uploadPreprocess: UploadPreprocessService,
    private readonly backgroundUploadOrchestrator: BackgroundUploadOrchestratorService,
  ) { }

  get(id: string): Observable<DocumentApiModel> {
    return this.http.get<DocumentApiModel>(environment.apiUrl + id)
      .pipe(map((response: DocumentApiModel) => {
        return plainToClass(DocumentApiModel, response);
    }));
  }

  getCollection(queryParamsApiModel?: QueryParamsApiModel) {
    const params = this.params.applyParameters(queryParamsApiModel);
    return this.http.get<ResultListApiModel<DocumentApiModel>>(environment.apiUrl + this.resource, {params})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<DocumentApiModel>(DocumentApiModel), result as object)));
  }

  put(documentAM: DocumentApiModel): Observable<number | DocumentApiModel> {
    return this.http.put<DocumentApiModel>(environment.apiUrl + documentAM['@id'], {description: documentAM.description}, {
      reportProgress: true,
      observe: 'events',
    }).pipe(
      map(event => {
        if (event.type === HttpEventType.UploadProgress) {
          // Emituj procentowy postęp
          return Math.round((event.loaded / (event.total || 1)) * 100);
        } else if (event.type === HttpEventType.Response) {
          // Po zakończeniu zwróć model
          const resultDocument =  plainToClass(DocumentApiModel, event.body);
          resultDocument.file = documentAM.file;
          return resultDocument;
        }
        return 0; // Wartość domyślna
      })
    );
  }

  delete(id: string) {
    return this.http.delete(environment.apiUrl + id)
  }

  upload(file: FormData): Observable<DocumentApiModel> {
    return this.http.post<DocumentApiModel>(environment.apiUrl + this.resource, file);
  }

  uploadItem(image: File, caption: string = null): Observable<DocumentApiModel> {
    return from(this.uploadPreprocess.prepare(image, 'image', 'documents.item')).pipe(
      switchMap((preparedUpload) => {
        const fd = DocumentRestService.prepareFormData(preparedUpload.file, MediaTypeEnum.IMAGE, caption);
        void this.backgroundUploadOrchestrator.enqueue(preparedUpload, this.resource);
        void this.backgroundUploadOrchestrator.markUploading(preparedUpload.clientUploadId);
        return this.upload(fd).pipe(
          map((response: DocumentApiModel) => {
            const parsed = plainToClass(DocumentApiModel, response);
            void this.backgroundUploadOrchestrator.markUploaded(preparedUpload.clientUploadId, parsed['@id']);
            return parsed;
          }),
          tap(() => this.uploadPreprocess.markUploaded(preparedUpload)),
          catchError((error) => {
            this.uploadPreprocess.markFailed(preparedUpload, error);
            void this.backgroundUploadOrchestrator.markFailed(preparedUpload.clientUploadId, error?.message || 'Upload failed');
            return throwError(() => error);
          })
        );
      })
    );
  }

  uploadItemWithProgress(type: string, documentFile: File, description: string = null): Observable<number | DocumentApiModel> {
    const category = this.resolvePreprocessCategory(type, documentFile);
    return from(this.uploadPreprocess.prepare(documentFile, category, 'documents.item_with_progress')).pipe(
      switchMap((preparedUpload) => {
        const fd = DocumentRestService.prepareFormData(preparedUpload.file, type, description);
        void this.backgroundUploadOrchestrator.enqueue(preparedUpload, this.resource);
        void this.backgroundUploadOrchestrator.markUploading(preparedUpload.clientUploadId);
        return this.http.post<DocumentApiModel>(environment.apiUrl + this.resource, fd, {
          reportProgress: true,
          observe: 'events',
        }).pipe(
          map(event => {
            if (event.type === HttpEventType.UploadProgress) {
              // Emituj procentowy postęp
              return Math.round((event.loaded / (event.total || 1)) * 100);
            } else if (event.type === HttpEventType.Response) {
              // Po zakończeniu zwróć model
              this.uploadPreprocess.markUploaded(preparedUpload);
              const parsed = plainToClass(DocumentApiModel, event.body);
              void this.backgroundUploadOrchestrator.markUploaded(preparedUpload.clientUploadId, parsed['@id']);
              return parsed;
            }
            return 0; // Wartość domyślna
          }),
          catchError((error) => {
            this.uploadPreprocess.markFailed(preparedUpload, error);
            void this.backgroundUploadOrchestrator.markFailed(preparedUpload.clientUploadId, error?.message || 'Upload failed');
            return throwError(() => error);
          })
        );
      })
    );
  }

  private static prepareFormData(file: File, type: string, description: string): FormData {
    const fd = new FormData();
    fd.append('file', file, file.name);
    fd.append('type', type);
    fd.append('description', description);
    return fd;
  }

  getSubresourceCollection(ownerResource: string, queryParamsApiModel: QueryParamsApiModel = new QueryParamsApiModel()) {
    const params = this.params.applyParameters(queryParamsApiModel);
    return this.http.get<ResultListApiModel<DocumentApiModel>>(environment.apiUrl + ownerResource  + this.resource, {params})
      .pipe(map(result => plainToClassFromExist(new ResultListApiModel<DocumentApiModel>(DocumentApiModel), result as object)));
  }

  private resolvePreprocessCategory(type: string, file: File): UploadPreprocessCategory {
    if (type === MediaTypeEnum.IMAGE || file?.type?.startsWith('image/')) {
      return 'image';
    }

    if (type === MediaTypeEnum.VIDEO || file?.type?.startsWith('video/')) {
      return 'video';
    }

    return 'document';
  }
}
